import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, defer, distinctUntilChanged, filter, finalize, ignoreElements, map, merge, retry, share, tap, timer } from 'rxjs';
import { DeviceService } from '../devices/device.service';
import { WebsocketService } from '../websocket/websocket.service';

export interface ModuleState {
  cfg?: any;
  status?: any;
}

// Margen para que el socket sobreviva a la navegación dashboard → ajustes sin reconectar ni repedir.
const KEEP_ALIVE_MS = 5000;
// Pausa tras agotar los reintentos del WebsocketService antes de volver a intentarlo.
const RETRY_MS = 30000;

type LoadMode = 'initial' | 'reload' | 'refresh';

// Estado por dispositivo compartido entre pantallas: cada módulo se lee una vez por REST (caché del backend,
// sin localdata, para no despertar al dispositivo) y desde ahí lo mantiene el websocket.
@Injectable({
  providedIn: 'root'
})
export class DeviceStateService {

  private serial = '';
  private modules = new BehaviorSubject<Record<string, ModuleState>>({});
  // módulo → level, para releerlos al recuperar el socket.
  private requested = new Map<string, string>();
  private live$?: Observable<never>;

  constructor(private _device: DeviceService, private _ws: WebsocketService) { }

  // Sin level el módulo solo se rellena por websocket (p. ej. 'rt'). La suscripción mantiene el socket abierto.
  select(serial: string, module: string, level?: string): Observable<ModuleState> {
    this.use(serial);
    if (level && !this.requested.has(module)) {
      this.requested.set(module, level);
      this.load(serial, module, level, 'initial');
    }
    return merge(
      this.live$!,
      this.modules.pipe(map(m => m[module]), filter(Boolean), distinctUntilChanged()),
    );
  }

  // Relectura puntual al dispositivo cuando el websocket no confirma una acción (no es polling).
  refresh(serial: string, module: string, level: string): void {
    this.load(serial, module, level, 'refresh');
  }

  // initial: siembra al entrar; reload: tras recuperar el socket; refresh: lectura al dispositivo (localdata).
  private load(serial: string, module: string, level: string, mode: LoadMode): void {
    // '_' evita el 304 por ETag al repetir la misma URL tras resetear el estado.
    // relays solo trae cfg/stat del relé (con total_minutes) con elements=true, en elements[0].data.
    const elements = module === 'relays';
    const params = { _: Date.now(), ...(elements && { elements: true }), ...(mode === 'refresh' && { localdata: true }) };
    this._device.getModuleInfo(serial, module, level, params).subscribe({
      // Un mensaje del websocket que haya llegado antes es más reciente que la caché: no se pisa.
      // Sin localdata la BD guarda el estado en 'stat'; el GET al dispositivo lo llama 'status'.
      next: resp => {
        const item = resp?.items?.[0] ?? (elements ? resp?.elements?.[0]?.data : undefined) ?? resp;
        this.patch(serial, module, { cfg: item?.cfg, status: item?.status ?? item?.stat }, mode === 'initial');
        this.ensure(serial, module);
      },
      error: () => {
        // Solo la siembra deja el módulo sin pedir; si falla una relectura, el estado previo sigue valiendo.
        if (mode === 'initial') this.requested.delete(module);
        this.ensure(serial, module);
      },
    });
  }

  patch(serial: string, module: string, part: ModuleState, keepExisting = false): void {
    if (serial !== this.serial || (part?.cfg === undefined && part?.status === undefined)) return;
    const prev = this.modules.value[module] ?? {};
    const mergeKey = (a: any, b: any) => b === undefined ? a : keepExisting ? { ...b, ...a } : { ...a, ...b };
    this.modules.next({
      ...this.modules.value,
      [module]: { cfg: mergeKey(prev.cfg, part?.cfg), status: mergeKey(prev.status, part?.status) },
    });
  }

  // La pantalla sale del estado de carga aunque la respuesta no traiga nada.
  private ensure(serial: string, module: string): void {
    if (serial === this.serial && !this.modules.value[module]) this.modules.next({ ...this.modules.value, [module]: {} });
  }

  private use(serial: string): void {
    if (serial === this.serial && this.live$) return;
    this.serial = serial;
    this.reset();
    // defer: cada reconexión reabre la sesión, por si otra pantalla ha usado el WebsocketService entretanto.
    this.live$ = defer(() => this._ws.connect(serial)).pipe(
      tap(msg => msg?.module && this.patch(serial, msg.module, { cfg: msg.data?.cfg, status: msg.data?.stat ?? msg.data?.status })),
      ignoreElements(),
      // Agotados los reintentos se sigue intentando, y al volver se relee lo perdido mientras no había socket.
      retry({ delay: () => timer(RETRY_MS).pipe(tap(() => this.reload(serial))) }),
      // Sin socket el estado deja de ser fiable: la próxima pantalla vuelve a leerlo.
      finalize(() => this.serial === serial && this.reset()),
      share({ resetOnRefCountZero: () => timer(KEEP_ALIVE_MS) }),
    );
  }

  private reload(serial: string): void {
    if (serial === this.serial) this.requested.forEach((level, module) => this.load(serial, module, level, 'reload'));
  }

  private reset(): void {
    this.requested.clear();
    this.modules.next({});
  }

}
